import { useCallback, useEffect, useState } from "react";
import type { PushSubscriptionInput } from "@boo/shared";

const SW_URL = "/sw.js";

const ua = () => navigator.userAgent;

export const isIos = () =>
  /iPad|iPhone|iPod/.test(ua()) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** LINE's in-app browser cannot receive web push at all. */
export const isLineInApp = () => /\bLine\//i.test(ua());

/** iOS only delivers web push to a page added to the Home Screen. */
export const needsInstallForPush = () => isIos() && !isStandalone();

export const isPushSupported = () =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

function keyBytes(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url + "=".repeat((4 - (base64Url.length % 4)) % 4);
  const raw = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// Pick fields explicitly: Chrome's toJSON() adds expirationTime, which the
// API's strict DTO rejects.
const toInput = (sub: PushSubscription): PushSubscriptionInput => {
  const { endpoint, keys } = sub.toJSON() as PushSubscriptionInput;
  return { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } };
};

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  return reg ? reg.pushManager.getSubscription() : null;
}

async function subscribe(vapidKey: string): Promise<PushSubscriptionInput> {
  if ((await Notification.requestPermission()) !== "granted") {
    throw new Error("Notifications are blocked for this site");
  }
  const reg = await navigator.serviceWorker.register(SW_URL);
  await navigator.serviceWorker.ready;
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(vapidKey),
    }));
  return toInput(sub);
}

export type PushState =
  | "unsupported" // nothing we can do here (incl. LINE in-app browser)
  | "needs-install" // iOS Safari tab: add to Home Screen first
  | "unavailable" // server has no VAPID keys
  | "off"
  | "on"
  | "blocked";

interface PushBinding {
  /** Server's VAPID public key, or null when push is not configured. */
  fetchKey: () => Promise<string | null>;
  register: (sub: PushSubscriptionInput) => Promise<void>;
  unregister: (endpoint: string) => Promise<void>;
}

/**
 * Opt-in Web Push for the current browser. Re-registers an existing
 * subscription on mount so the server always binds it to the current
 * account / bill person.
 */
export function usePushSubscription(binding: PushBinding) {
  const [state, setState] = useState<PushState | "loading">("loading");
  const { fetchKey, register, unregister } = binding;

  useEffect(() => {
    let cancelled = false;
    const set = (s: PushState) => !cancelled && setState(s);
    async function init() {
      try {
        if (isLineInApp()) return set("unsupported");
        if (needsInstallForPush()) return set("needs-install");
        if (!isPushSupported()) return set("unsupported");
        let key: string | null = null;
        try {
          key = await fetchKey();
        } catch {
          // treated as unavailable below
        }
        if (key === null) return set("unavailable");
        if (Notification.permission === "denied") return set("blocked");
        const sub = await currentSubscription();
        if (sub && Notification.permission === "granted") {
          try {
            await register(toInput(sub));
          } catch {
            // re-register is best effort
          }
          return set("on");
        }
        set("off");
      } catch {
        set("unsupported");
      }
    }
    void init();
    return () => {
      cancelled = true;
    };
  }, [fetchKey, register]);

  const enable = useCallback(async () => {
    const key = await fetchKey();
    if (!key) throw new Error("Notifications are not available right now");
    try {
      await register(await subscribe(key));
      setState("on");
    } catch (err) {
      if (Notification.permission === "denied") setState("blocked");
      throw err;
    }
  }, [fetchKey, register]);

  const disable = useCallback(async () => {
    const sub = await currentSubscription();
    if (sub) {
      try {
        await unregister(sub.endpoint);
      } catch {
        // server cleanup is best effort
      }
      try {
        await sub.unsubscribe();
      } catch {
        // already unsubscribed
      }
    }
    setState("off");
  }, [unregister]);

  return { state, enable, disable };
}
